import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, User, Calendar, Activity, Loader2, FolderOpen } from 'lucide-react';

export default function PatientHistoryPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/patients/${patientId}/history`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  if (!data) return <div className="text-center py-12 text-stone-500">No patient data found</div>;

  const topFinding = (c) => {
    if (!c.analysis?.diseases?.length) return '—';
    const top = c.analysis.diseases.reduce((a, b) => a.probability > b.probability ? a : b);
    return `${top.name} (${(top.probability * 100).toFixed(0)}%)`;
  };

  return (
    <div className="space-y-6" data-testid="patient-history-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">Patient History</h1>
          <p className="text-stone-500 mt-1">All cases for this patient</p>
        </div>
      </div>

      {/* Patient info card */}
      <Card className="border-stone-200 shadow-sm bg-[#1A4D2E]/5">
        <CardContent className="pt-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="w-14 h-14 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
              <User className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-heading font-medium text-stone-900">{data.patient_name}</h2>
                <Badge variant="outline" className="font-mono text-[#1A4D2E] border-[#1A4D2E]">{data.patient_id}</Badge>
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                <span>{data.patient_age} years old</span>
                <span>{data.patient_gender}</span>
                <span className="flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> {data.total_cases} case{data.total_cases !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <Button onClick={() => navigate('/new-case?patientId=' + data.patient_id)} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2 rounded-lg">
              <Activity className="w-4 h-4" /> New Case for Patient
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cases timeline */}
      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Case Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <Table data-testid="patient-cases-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Case ID</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Clinical Notes</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Top Finding</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.cases.map(c => (
                <TableRow key={c.id} data-testid={`patient-case-${c.id}`} className="cursor-pointer hover:bg-stone-50" onClick={() => navigate(`/case/${c.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-stone-400" />
                      <span className="text-sm text-stone-600">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-stone-500">{c.id.slice(0, 8)}...</TableCell>
                  <TableCell className="text-sm text-stone-600 max-w-[200px] truncate">{c.clinical_notes || '—'}</TableCell>
                  <TableCell className="text-sm text-stone-700">{topFinding(c)}</TableCell>
                  <TableCell>
                    <Badge variant={c.status === 'analyzed' ? 'default' : 'outline'}
                      className={c.status === 'analyzed' ? 'bg-[#1A4D2E] text-white' : ''}>
                      {c.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); navigate(`/case/${c.id}`); }}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
